import React, {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';

import { authClient } from 'src/modules/auth';

import { useDatadog } from '../Datadog/Datadog';
import { PageLoader } from '../PageLoader';

interface User {
  email: string;
  candidateId: string;
}

const UserContext = createContext<User | undefined>(undefined);

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const { setDatadogUser } = useDatadog();
  const [user, setUser] = useState<User>({ candidateId: '', email: '' });

  useEffect(() => {
    authClient.user().then((response) => {
      // This code may run in production before the login redirect kicks in.
      // We stall the user with our `PageLoader` until they are redirected.
      // istanbul ignore else
      if (response) {
        // Matches SEEK-Jobs/online-identity-sku-plugin#24.
        const candidateId =
          response['http://seek/claims/user_id'] ??
          // This will stop working when `sub` switches from the old numeric
          // SEEK ID to a new UUID.
          response.sub.slice('auth0|'.length);

        setUser({
          candidateId,
          email: response.email,
        });

        setDatadogUser(candidateId);
      }
    });
  }, [setDatadogUser]);

  if (!user.email) {
    return <PageLoader />;
  }

  return <UserContext.Provider value={user}>{children}</UserContext.Provider>;
};

export const useUser = () => {
  const user = useContext(UserContext);

  if (!user) {
    throw new Error('useUser must be wrapped in UserProvider.');
  }

  return user;
};
